export const CHECKOUT_URL = 'https://diabetes-en.impultienda.ar/checkout/'

export const PRODUCT_IMAGE =
  'https://cdn.magicpatterns.com/uploads/7cQu77fUvJW83c9hjSy89r/DIABETES-EN.png'

export const AUTHOR_IMAGE =
  'https://cdn.magicpatterns.com/uploads/msmopMyuWFtQFx7M8a3SLW/Dra_Karito_real.png'

export const heroStats = [
  { value: '+300', label: 'Ready-to-use algorithms' },
  { value: '+2,000', label: 'Physicians trust it' },
  { value: '2 min', label: 'Per patient' },
  { value: '99%', label: 'Recommend it' },
]

export const clinicalQuestions = [
  'Do I keep metformin, or is it time to intensify treatment?',
  'Would a GLP-1 receptor agonist or an SGLT2 inhibitor be the better choice?',
  'Should I start insulin now, or can I wait?',
  'What if the patient also has kidney disease?',
  'What if they also have heart failure?',
  'Am I actually following the most current recommendations?',
]

export const timeWasters = [
  'Open multiple guidelines',
  'Search for recommendations',
  'Compare algorithms',
  'Lose valuable time',
]

export const decisionPathway = [
  'Diagnosis',
  'Initial Treatment',
  'Treatment Intensification',
  'Cardiovascular Protection',
  'Kidney Protection',
  'Insulin Therapy',
  'Follow-Up',
]

export const beforeItems = [
  'You reviewed multiple guidelines just to confirm a decision.',
  'You hesitated between different treatment options.',
  'You avoided starting insulin because you weren’t completely confident.',
  'Complex patients consumed a large portion of your consultation time.',
  'You worried about overlooking an important recommendation.',
]

export const afterItems = [
  'You follow a clear algorithm from diagnosis to follow-up.',
  'You select treatment according to the patient’s clinical profile.',
  'You make decisions with greater speed and consistency.',
  'You manage patients with kidney disease or cardiovascular risk with more confidence.',
  'You walk into every consultation with a system ready to use.',
]

export const familiarSituations = [
  {
    icon: '🩺',
    text: 'You have a patient sitting in front of you and you’re unsure whether to prescribe a GLP-1 receptor agonist, an SGLT2 inhibitor, or start insulin.',
  },
  {
    icon: '📚',
    text: 'You spend too much time reviewing ADA, EASD, or KDIGO guidelines just to confirm a clinical decision.',
  },
  {
    icon: '⚠️',
    text: 'Patients with multiple comorbidities make every consultation more complex.',
  },
  {
    icon: '🧠',
    text: 'You feel that your decisions depend more on memory than on a structured process.',
  },
  {
    icon: '🔍',
    text: 'You’re concerned about forgetting an important test or treatment adjustment during follow-up.',
  },
  {
    icon: '🗺️',
    text: 'You wish you had a clear method you could apply in any consultation without improvising.',
  },
]

export type Volume = {
  number: number
  title: string
  promise: string
  outcomesLabel: string
  outcomes: string[]
  accent: string
}

export const volumes: Volume[] = [
  {
    number: 1,
    title: 'Diagnosis Without Doubt',
    promise:
      'Learn to accurately identify who has diabetes, who is at risk, and who needs a completely different clinical approach.',
    outcomesLabel: 'By the end of this volume, you’ll be able to:',
    outcomes: [
      'Diagnose with greater confidence from the very first consultation.',
      'Differentiate type 2 diabetes, LADA, and MODY without unnecessary confusion.',
      'Avoid diagnostic mistakes that can delay appropriate treatment.',
      'Apply current diagnostic criteria without constantly consulting multiple guidelines.',
    ],
    accent: 'bg-navy-800',
  },
  {
    number: 2,
    title: 'The Right Treatment From Day One',
    promise:
      'Choose the right therapeutic strategy for each patient and avoid starting with the wrong treatment pathway.',
    outcomesLabel: 'You’ll learn how to:',
    outcomes: [
      'Define personalized HbA1c targets.',
      'Choose initial treatment with greater confidence.',
      'Know exactly when treatment intensification is needed.',
      'Adapt therapy according to each patient’s clinical profile.',
    ],
    accent: 'bg-clinic-600',
  },
  {
    number: 3,
    title: 'Protect the Heart While Controlling Glucose',
    promise:
      'Learn when to use SGLT2 inhibitors and GLP-1 receptor agonists to deliver more than glucose control.',
    outcomesLabel: 'You’ll discover how to:',
    outcomes: [
      'Reduce cardiovascular risk.',
      'Choose the appropriate medication based on the patient’s clinical profile.',
      'Apply ADA and ESC recommendations more practically.',
      'Protect target organs from earlier stages of disease.',
    ],
    accent: 'bg-alert-600',
  },
  {
    number: 4,
    title: 'Preserve Kidney Function With Every Decision',
    promise:
      'Manage patients with kidney disease with greater confidence and fewer dosing uncertainties.',
    outcomesLabel: 'You’ll be able to:',
    outcomes: [
      'Adjust medications according to eGFR.',
      'Reduce the risk of inappropriate dosing.',
      'Apply KDIGO recommendations in a practical way.',
      'Protect kidney function while optimizing diabetes treatment.',
    ],
    accent: 'bg-[#5b3fbf]',
  },
  {
    number: 5,
    title: 'Master Insulin Without Fear',
    promise:
      'Turn one of the most complex treatment decisions into a clear and repeatable process.',
    outcomesLabel: 'You’ll learn how to:',
    outcomes: [
      'Know when insulin should be initiated.',
      'Titrate doses more confidently.',
      'Recognize and reduce the risk of hypoglycemia.',
      'Adjust treatment according to patient response and clinical evolution.',
    ],
    accent: 'bg-[#e2600f]',
  },
  {
    number: 6,
    title: 'Manage Diabetes for the Long Term',
    promise:
      'Implement a structured follow-up approach designed to reduce missed opportunities and improve long-term clinical management.',
    outcomesLabel: 'You’ll be able to:',
    outcomes: [
      'Manage older adults and complex patients.',
      'Identify complications earlier.',
      'Organize annual follow-up.',
      'Maintain patient management with a clear and structured system.',
    ],
    accent: 'bg-[#0e7c8c]',
  },
]

export type Bonus = {
  index: number
  title: string
  description: string
  listLabel: string
  items: string[]
  closing?: string
  value: string
  image: string
}

export const bonuses: Bonus[] = [
  {
    index: 1,
    title: 'Smart Renal Dose Adjustment Guide',
    description:
      'Prescribe more confidently in patients with kidney disease using a visual color-coded system designed to help you quickly review medication considerations based on kidney function.',
    listLabel: 'A practical visual reference covering:',
    items: ['Metformin', 'SGLT2 inhibitors', 'DPP-4 inhibitors', 'GLP-1 receptor agonists'],
    value: '$27 USD',
    image:
      'https://cdn.magicpatterns.com/uploads/72WLaDKSZZ6R2XeByjB9aV/BONUS_1_DIABETES-EN.png',
  },
  {
    index: 2,
    title: 'Express Insulin Initiation & Titration System',
    description:
      'Ready-to-use protocols to help you initiate, transition, and adjust insulin using structured clinical pathways.',
    listLabel: 'Designed to help you approach:',
    items: [
      'Insulin initiation',
      'Dose titration',
      'Treatment intensification',
      'Common insulin transitions',
      'Hypoglycemia considerations',
    ],
    closing: 'Without having to build the process from scratch every time.',
    value: '$37 USD',
    image:
      'https://cdn.magicpatterns.com/uploads/wj2y1EwRewYzp763DRGTr1/BONUS_2_DIABETES-EN.png',
  },
  {
    index: 3,
    title: 'Master Annual Diabetes Checkup Checklist',
    description: 'Never overlook an important follow-up assessment again.',
    listLabel: 'A structured checklist including:',
    items: [
      'Eye screening',
      'Kidney monitoring',
      'Foot assessment',
      'Cardiovascular risk review',
      'Laboratory monitoring',
      'Medication review',
    ],
    closing: 'Designed to help you organize a more complete annual review in minutes.',
    value: '$27 USD',
    image:
      'https://cdn.magicpatterns.com/uploads/9m4LCL9Mh6XtTPWY2CvsQ3/BONUS_3_DIABETES-EN.png',
  },
  {
    index: 4,
    title: 'Clinical Script to Overcome Fear of Insulin',
    description:
      'Learn exactly how to approach the conversation when a patient is afraid of starting insulin.',
    listLabel: 'Practical communication frameworks to help you:',
    items: [
      'Address common myths',
      'Respond to patient concerns',
      'Discuss fear of injections',
      'Improve treatment understanding',
      'Support shared decision-making',
    ],
    closing:
      'Because sometimes the barrier isn’t knowing what to prescribe — it’s helping the patient feel ready to begin.',
    value: '$20 USD',
    image:
      'https://cdn.magicpatterns.com/uploads/k9dTn3XAF56xyvhVQv7JNw/BONUS_4_DIABETES-EN.png',
  },
  {
    index: 5,
    title: 'Quick Map of Medications That Disrupt Glucose Control',
    description:
      'Quickly identify medications that may affect glycemic control before they create unnecessary confusion.',
    listLabel: 'A practical reference covering:',
    items: [
      'Corticosteroids',
      'Diuretics',
      'Beta-blockers',
      'Antipsychotics',
      'Other medications requiring special consideration',
    ],
    closing:
      'Designed to help you recognize potential factors affecting glucose control during routine clinical care.',
    value: '$29 USD',
    image:
      'https://cdn.magicpatterns.com/uploads/e2Pi9xz5MJkjRZaa23xxSf/BONUS_5_DIABTES-EN.png',
  },
]

export const testimonials = [
  {
    quote:
      'I now walk into the consultation with a much clearer method. I spend less time searching through different guidelines to confirm what I already suspected.',
    name: 'María P.',
    country: 'Colombia 🇨🇴',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/184dbc7c-6017-4c78-8160-3a0b11c4cd9a.jpg',
  },
  {
    quote:
      'The kidney disease volume completely changed how I organize medication considerations in more complex patients.',
    name: 'Roberto L.',
    country: 'Mexico 🇲🇽',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/e36d12a0-49de-4e9e-993c-2be262599724.jpg',
  },
  {
    quote:
      'The insulin pathway alone gave me a much more structured way to approach a decision I used to find complicated.',
    name: 'Carolina G.',
    country: 'Argentina 🇦🇷',
    image:
      'https://cdn.magicpatterns.com/patterns/generated-images/d238a408-bb6a-49c5-836f-a9af8a8115a9.jpg',
  },
]

export const faqs = [
  {
    question: 'How will I receive the system?',
    answer:
      'You’ll receive immediate digital access after your payment is confirmed. You can access and download the included digital materials right away.',
  },
  {
    question: 'Does it work in every country?',
    answer:
      'The system is based on internationally recognized clinical guidelines and evidence. However, local regulations, medication availability, formularies, prescribing requirements, and standards of care may vary by country and region. The material should be used as an educational resource alongside local clinical guidance and professional judgment.',
  },
  {
    question: 'Do I need to be a specialist?',
    answer:
      'No. The system is designed for healthcare professionals involved in the care of patients with diabetes who want a more structured way to organize clinical decision-making. However, it is not intended to replace specialist consultation when appropriate.',
  },
  {
    question: 'Do I have to pay again later?',
    answer:
      'No. There is no monthly subscription for the current product edition. You make one payment and receive access to the digital materials included with your purchase.',
  },
  {
    question: 'Can I access it from my phone?',
    answer:
      'Yes. The materials are designed for digital access and can be viewed on compatible computers, tablets, and mobile devices.',
  },
  {
    question: 'Is this a replacement for clinical judgment?',
    answer:
      'No. The Clinical Decision System is an educational decision-support resource. Clinical decisions should always be based on the individual patient, professional judgment, current evidence, local regulations, and when appropriate, specialist consultation.',
  },
]

export const recentPurchases = [
  { name: 'Dr. Andrés Molina', country: 'Colombia' },
  { name: 'Dra. Valentina Ríos', country: 'Chile' },
  { name: 'Dr. Julián Herrera', country: 'Mexico' },
  { name: 'Dra. Camila Torres', country: 'Peru' },
  { name: 'Dr. Sebastián Ortega', country: 'Argentina' },
  { name: 'Dra. Lucía Fernández', country: 'Spain' },
  { name: 'Dr. Mateo Guzmán', country: 'Ecuador' },
  { name: 'Dra. Daniela Castro', country: 'Uruguay' },
  { name: 'Dr. Ricardo Peña', country: 'Costa Rica' },
  { name: 'Dra. Paola Jiménez', country: 'Guatemala' },
]
